import { supabase } from '@/lib/supabase';
import type { Session } from '@supabase/supabase-js';
import { create } from 'zustand';

export type Role = 'operator' | 'admin';

export interface AuthUser {
  id: string;
  name: string;
}

interface SignInResult {
  error: Error | null;
}

interface SignUpResult {
  // null when sign-up succeeded and the user was auto-signed-in (auto-confirm
  // ON or local dev). Set when sign-up succeeded but Supabase requires email
  // confirmation before the user can sign in.
  needsEmailConfirmation: boolean;
  error: Error | null;
}

interface AuthState {
  isAuthenticated: boolean;
  role: Role | null;
  user: AuthUser | null;
  // Sync mutators — used by tests, by Supabase listener, and by internal actions.
  login: (role: Role, user: AuthUser) => void;
  logout: () => void;
  // Promote / demote the role after the API tells us the truth. Used by
  // useHydrateRole() in AppShell on mount + on me.get refetch.
  setRole: (role: Role) => void;
  // Apply a Supabase Session (or null to clear) — used by initialize() listener.
  applySession: (session: Session | null) => void;
  // Async Supabase actions — consumed by login page + AppShell logout button.
  signInWithPassword: (email: string, password: string) => Promise<SignInResult>;
  // Self-registration. Role is NOT settable from the client — Supabase Auth
  // creates an auth.users row only. The operators row is auto-created
  // server-side by migration 0007 (trigger handle_new_auth_user, role
  // default 'junior'). Admins are minted only via SQL or backend admin
  // endpoint, never from the front.
  signUp: (email: string, password: string) => Promise<SignUpResult>;
  signOut: () => Promise<void>;
  // Called once at app boot from main.tsx — restores existing session AND
  // subscribes to onAuthStateChange so the store stays in sync with Supabase
  // across token refresh, cross-tab sign-out, etc.
  initialize: () => Promise<void>;
  // Subset of initialize() kept for tests that only care about session restore.
  restoreSession: () => Promise<void>;
}

// Provisional role assigned the moment a session lands. Real role comes from
// `me.get` via useHydrateRole() and overrides this within ~one round-trip.
// We default to 'operator' (low privilege) so an admin briefly sees the
// operator nav rather than a junior briefly seeing admin routes.
const sessionToRole = (_session: Session): Role => 'operator';

const sessionToAuthUser = (session: Session): AuthUser => ({
  id: session.user.id,
  name: session.user.email ?? 'Unknown',
});

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  role: null,
  user: null,

  login: (role, user) => set({ isAuthenticated: true, role, user }),
  logout: () => set({ isAuthenticated: false, role: null, user: null }),
  setRole: (role) => set({ role }),

  applySession: (session) => {
    if (!session) {
      set({ isAuthenticated: false, role: null, user: null });
      return;
    }
    const newUser = sessionToAuthUser(session);
    const current = get();
    // Token refresh / INITIAL_SESSION events fire repeatedly during a session.
    // If the user id is the same as before AND we already have a role hydrated,
    // we MUST NOT clobber it back to the provisional 'operator' — that would
    // make the admin nav flicker out on every Supabase auth event.
    const sameUser = current.user?.id === newUser.id;
    const role: Role = sameUser && current.role ? current.role : sessionToRole(session);
    set({ isAuthenticated: true, role, user: newUser });
  },

  signInWithPassword: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return { error };
    }
    get().applySession(data.session);
    return { error: null };
  },

  signUp: async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) {
      return { needsEmailConfirmation: false, error };
    }
    // When the project has "Confirm email" ON, signUp returns user but
    // session === null. The user must confirm their email before signing in.
    if (data.session) {
      get().applySession(data.session);
      return { needsEmailConfirmation: false, error: null };
    }
    return { needsEmailConfirmation: true, error: null };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    get().applySession(null);
  },

  restoreSession: async () => {
    const { data } = await supabase.auth.getSession();
    get().applySession(data.session);
  },

  initialize: async () => {
    const { data } = await supabase.auth.getSession();
    get().applySession(data.session);
    supabase.auth.onAuthStateChange((_event, session) => {
      get().applySession(session);
    });
  },
}));
